import React, { useState, useEffect, useRef, useCallback } from "react";
import Icon from "../../../../components/AppIcon";
import axiosInstance from "../../../../utils/axiosInstance";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

// ─── Mini spinner ──────────────────────────────────────────────────────────────
const Spin = ({ size = 14, cls = "border-blue-600" }) => (
  <div
    className={`rounded-full border-2 border-gray-200 border-t-current animate-spin ${cls}`}
    style={{ width: size, height: size }}
  />
);

// ─── Test type badge ───────────────────────────────────────────────────────────
const TypeBadge = ({ type }) => {
  const cfg = {
    RADIOLOGY: "bg-violet-100 text-violet-700",
    PATHOLOGY: "bg-blue-100 text-blue-700",
  };

  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold ${
        cfg[type] || "bg-gray-100 text-gray-600"
      }`}
    >
      {type}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// EditOrderModal
// Props:
//   order       – { id, orderNumber, paymentStatus, finalAmount, ... }
//   onClose     – () => void
//   onSaved     – (updatedOrder) => void
// ─────────────────────────────────────────────────────────────────────────────
export default function EditOrderModal({ order, onClose, onSaved }) {
  // ── state ──────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // current line items
  // { id, memberId, testId, packageId, name, type, category, price, basePrice }
  const [items, setItems] = useState([]);

  // order level fields
  const [discount, setDiscount] = useState("");
  const [collCharge, setCollCharge] = useState("");
  const [remarks, setRemarks] = useState("");

  // search panel
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchRes, setSearchRes] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);

  // per-item price edit
  const [editingPrice, setEditingPrice] = useState(null);
  const [regenerateInvoice, setRegenerateInvoice] = useState(false);

  const isPaid = order?.paymentStatus && order.paymentStatus !== "PENDING";

  // ── fetch order's current tests ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await axiosInstance.get(
          `${API_BASE}/api/orders/${order.id}/edit-data`,
          { withCredentials: true }
        );

        const d = res.data;

        setItems(d.items || []);
        setDiscount(d.discountAmount != null ? String(d.discountAmount) : "");
        setCollCharge(d.collectionCharge != null ? String(d.collectionCharge) : "");
        setRemarks(d.remarks || "");
      } catch (e) {
        setError(e.response?.data?.message || "Failed to load order data");
      } finally {
        setLoading(false);
      }
    })();
  }, [order.id]);

  // ── normalize search response ──────────────────────────────────────────────
  const normalizeSearchResults = (raw) => {
    // Case 1: raw itself is an array
    if (Array.isArray(raw)) {
      return raw.map((test) => ({
        ...test,
        category: test.category || "",
        basePrice: test.basePrice ?? test.actualPrice ?? test.price ?? 0,
        offerPrice: test.offerPrice ?? test.finalPrice ?? test.price ?? 0,
        price: test.price ?? test.finalPrice ?? test.offerPrice ?? 0,
        isPackage: test.isPackage ?? false,
        type: test.type || "PATHOLOGY",
      }));
    }

    // Case 2: direct arrays in known keys
    if (Array.isArray(raw?.tests)) {
      return raw.tests.map((test) => ({
        ...test,
        category: test.category || "",
        basePrice: test.basePrice ?? test.actualPrice ?? test.price ?? 0,
        offerPrice: test.offerPrice ?? test.finalPrice ?? test.price ?? 0,
        price: test.price ?? test.finalPrice ?? test.offerPrice ?? 0,
        isPackage: test.isPackage ?? false,
        type: test.type || "PATHOLOGY",
      }));
    }

    if (Array.isArray(raw?.data)) {
      return raw.data.map((test) => ({
        ...test,
        category: test.category || "",
        basePrice: test.basePrice ?? test.actualPrice ?? test.price ?? 0,
        offerPrice: test.offerPrice ?? test.finalPrice ?? test.price ?? 0,
        price: test.price ?? test.finalPrice ?? test.offerPrice ?? 0,
        isPackage: test.isPackage ?? false,
        type: test.type || "PATHOLOGY",
      }));
    }

    if (Array.isArray(raw?.results)) {
      return raw.results.map((test) => ({
        ...test,
        category: test.category || "",
        basePrice: test.basePrice ?? test.actualPrice ?? test.price ?? 0,
        offerPrice: test.offerPrice ?? test.finalPrice ?? test.price ?? 0,
        price: test.price ?? test.finalPrice ?? test.offerPrice ?? 0,
        isPackage: test.isPackage ?? false,
        type: test.type || "PATHOLOGY",
      }));
    }

    // Case 3: grouped response
    // {
    //   success: true,
    //   data: {
    //     "Heart": [{...}],
    //     "Blood": [{...}]
    //   }
    // }
    if (raw?.success && raw?.data && typeof raw.data === "object") {
      return Object.entries(raw.data).flatMap(([category, tests]) =>
        (Array.isArray(tests) ? tests : []).map((test) => ({
          ...test,
          category,
          basePrice: test.basePrice ?? test.actualPrice ?? test.price ?? 0,
          offerPrice: test.offerPrice ?? test.finalPrice ?? test.price ?? 0,
          price: test.price ?? test.finalPrice ?? test.offerPrice ?? 0,
          isPackage: test.isPackage ?? false,
          type: test.type || "PATHOLOGY",
        }))
      );
    }

    return [];
  };

  // ── test/package search ────────────────────────────────────────────────────
  const doSearch = useCallback(async (q) => {
    if (!q.trim()) {
      setSearchRes([]);
      return;
    }

    try {
      setSearching(true);

      const res = await axiosInstance.get(`${API_BASE}/api/tests/search`, {
        params: { q, limit: 20 },
        withCredentials: true,
      });

      const list = normalizeSearchResults(res.data);
      setSearchRes(list);
    } catch {
      setSearchRes([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(searchQ), 350);
    return () => clearTimeout(t);
  }, [searchQ, doSearch]);

  // close search on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── computed totals ────────────────────────────────────────────────────────
  const subtotal = items.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);
  const discAmt = parseFloat(discount) || 0;
  const collAmt = parseFloat(collCharge) || 0;
  const finalTotal = Math.max(0, subtotal - discAmt + collAmt);

  // ── helpers ────────────────────────────────────────────────────────────────
  const addTest = (test) => {
    const alreadyExists = items.some((i) =>
      test.isPackage ? i.packageId === test.id : i.testId === test.id
    );

    if (alreadyExists) return;

    setItems((prev) => [
      ...prev,
      {
        id: null,
        isNew: true,
        memberId: null,
        testId: test.isPackage ? null : test.id,
        packageId: test.isPackage ? test.id : null,
        name: test.name,
        type: test.type || "PATHOLOGY",
        category: test.category || "",
        basePrice:
          test.basePrice ??
          test.actualPrice ??
          test.offerPrice ??
          test.finalPrice ??
          test.price ??
          0,
        price:
          test.offerPrice ??
          test.finalPrice ??
          test.price ??
          test.basePrice ??
          test.actualPrice ??
          0,
      },
    ]);

    setSearchQ("");
    setSearchRes([]);
    setSearchOpen(false);
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updatePrice = (idx, val) => {
    setItems((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, price: val } : item))
    );
  };

  // ── save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (items.length === 0) {
      setError("Order must have at least one test.");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        items: items.map((i) => ({
          id: i.id,
          testId: i.testId,
          packageId: i.packageId,
          price: parseFloat(i.price) || 0,
          memberId: i.memberId,
        })),
        discountAmount: discAmt,
        collectionCharge: collAmt,
        finalAmount: finalTotal,
        totalAmount: subtotal,
        remarks: remarks,
        regenerateInvoice,
      };

      const res = await axiosInstance.put(
        `${API_BASE}/api/orders/${order.id}/edit-tests`,
        payload,
        { withCredentials: true }
      );

      onSaved?.(res.data.order);
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center">
              <Icon name="ClipboardEdit" size={18} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Modify Tests</h2>
              <p className="text-xs text-gray-500">
                {order.orderNumber} · {isPaid ? "Payment Done" : "Payment Pending"}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Icon name="X" size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Spin size={28} />
              <span className="text-xs text-gray-400">Loading order data…</span>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <Icon
                name="AlertCircle"
                size={15}
                className="text-red-500 flex-shrink-0 mt-0.5"
              />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {!loading && (
            <>
              {/* Add Test */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Add Test / Package
                </label>

                <div ref={searchRef} className="relative">
                  <div className="flex items-center gap-2 px-3 py-2.5 border border-gray-300 rounded-xl bg-white focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all">
                    {searching ? (
                      <Spin size={14} />
                    ) : (
                      <Icon
                        name="Search"
                        size={14}
                        className="text-gray-400 flex-shrink-0"
                      />
                    )}

                    <input
                      type="text"
                      placeholder="Search tests or packages to add…"
                      value={searchQ}
                      onChange={(e) => {
                        setSearchQ(e.target.value);
                        setSearchOpen(true);
                      }}
                      onFocus={() => setSearchOpen(true)}
                      className="flex-1 text-xs bg-transparent outline-none text-gray-800 placeholder-gray-400"
                    />

                    {searchQ && (
                      <button
                        onClick={() => {
                          setSearchQ("");
                          setSearchRes([]);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    )}
                  </div>

                  {/* Search dropdown */}
                  {searchOpen &&
                    (searchRes.length > 0 || (searchQ.length > 1 && !searching)) && (
                      <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-56 overflow-y-auto">
                        {searchRes.length === 0 ? (
                          <div className="px-4 py-6 text-xs text-center text-gray-400">
                            No tests found for "{searchQ}"
                          </div>
                        ) : (
                          searchRes.map((t) => {
                            const alreadyAdded = items.some((i) =>
                              t.isPackage ? i.packageId === t.id : i.testId === t.id
                            );

                            return (
                              <button
                                key={`${t.isPackage ? "p" : "t"}-${t.id}`}
                                onClick={() => !alreadyAdded && addTest(t)}
                                disabled={alreadyAdded}
                                className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0 ${
                                  alreadyAdded ? "opacity-40 cursor-not-allowed" : ""
                                }`}
                              >
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div
                                    className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                                      t.isPackage ? "bg-emerald-100" : "bg-blue-100"
                                    }`}
                                  >
                                    <Icon
                                      name={t.isPackage ? "Package" : "FlaskConical"}
                                      size={11}
                                      className={
                                        t.isPackage
                                          ? "text-emerald-600"
                                          : "text-blue-600"
                                      }
                                    />
                                  </div>

                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-gray-800 truncate">
                                      {t.name}
                                    </p>
                                    <p className="text-[10px] text-gray-400">
                                      {t.category || (t.isPackage ? "Package" : "Test")}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                  <TypeBadge type={t.type || "PATHOLOGY"} />
                                  <span className="text-xs font-semibold text-gray-700">
                                    {fmt(
                                      t.offerPrice ??
                                        t.finalPrice ??
                                        t.price ??
                                        t.basePrice ??
                                        t.actualPrice
                                    )}
                                  </span>
                                  {alreadyAdded ? (
                                    <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                      Added
                                    </span>
                                  ) : (
                                    <Icon name="Plus" size={14} className="text-blue-500" />
                                  )}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                </div>
              </div>

              {/* Tests list */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-gray-700">
                    Tests in Order
                    <span className="ml-1.5 text-[10px] text-gray-400 font-normal">
                      ({items.length})
                    </span>
                  </label>
                  {items.length > 0 && (
                    <span className="text-[10px] text-gray-400">
                      Click price to edit
                    </span>
                  )}
                </div>

                {items.length === 0 ? (
                  <div className="rounded-xl border-2 border-dashed border-gray-200 py-8 text-center">
                    <Icon
                      name="FlaskConical"
                      size={24}
                      className="text-gray-300 mx-auto mb-2"
                    />
                    <p className="text-xs text-gray-400">
                      No tests in this order. Add at least one above.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <div className="col-span-5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                        Test / Package
                      </div>
                      <div className="col-span-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                        Type
                      </div>
                      <div className="col-span-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide text-right">
                        Base Price
                      </div>
                      <div className="col-span-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide text-right">
                        Charge
                      </div>
                      <div className="col-span-1" />
                    </div>

                    {items.map((item, idx) => (
                      <div
                        key={`${item.id ?? "new"}-${idx}`}
                        className={`grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-gray-100 last:border-b-0 transition-colors ${
                          item.isNew ? "bg-blue-50/30" : "hover:bg-gray-50/40"
                        }`}
                      >
                        <div className="col-span-5 flex items-center gap-2 min-w-0">
                          <div
                            className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                              item.packageId ? "bg-emerald-100" : "bg-blue-100"
                            }`}
                          >
                            <Icon
                              name={item.packageId ? "Package" : "FlaskConical"}
                              size={11}
                              className={
                                item.packageId
                                  ? "text-emerald-600"
                                  : "text-blue-600"
                              }
                            />
                          </div>

                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">
                              {item.name}
                            </p>
                            {item.category && (
                              <p className="text-[10px] text-gray-400 truncate">
                                {item.category}
                              </p>
                            )}
                          </div>

                          {item.isNew && (
                            <span className="text-[8px] bg-blue-100 text-blue-600 font-bold px-1 py-0.5 rounded flex-shrink-0">
                              NEW
                            </span>
                          )}
                        </div>

                        <div className="col-span-2">
                          <TypeBadge type={item.type} />
                        </div>

                        <div className="col-span-2 text-right">
                          <span className="text-[10px] text-gray-400 line-through">
                            {fmt(item.basePrice)}
                          </span>
                        </div>

                        <div className="col-span-2 flex justify-end">
                          {editingPrice === idx ? (
                            <input
                              type="number"
                              value={item.price}
                              onChange={(e) => updatePrice(idx, e.target.value)}
                              onBlur={() => setEditingPrice(null)}
                              onKeyDown={(e) =>
                                e.key === "Enter" && setEditingPrice(null)
                              }
                              autoFocus
                              className="w-20 text-right text-xs font-semibold border border-blue-400 rounded-lg px-2 py-1 outline-none ring-2 ring-blue-500/20 text-gray-800"
                            />
                          ) : (
                            <button
                              onClick={() => setEditingPrice(idx)}
                              className={`text-xs font-semibold px-2 py-1 rounded-lg transition-colors ${
                                parseFloat(item.price) !== parseFloat(item.basePrice)
                                  ? "text-amber-700 bg-amber-50 hover:bg-amber-100"
                                  : "text-gray-800 hover:bg-gray-100"
                              }`}
                            >
                              {fmt(item.price)}
                            </button>
                          )}
                        </div>

                        <div className="col-span-1 flex justify-end">
                          <button
                            onClick={() => removeItem(idx)}
                            className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                            title="Remove test"
                          >
                            <Icon name="Trash2" size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Adjustments row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Discount Amount (₹)
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all">
                    <Icon name="Tag" size={13} className="text-gray-400" />
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      className="flex-1 text-xs bg-transparent outline-none text-gray-800"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Collection Charge (₹)
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all">
                    <Icon name="MapPin" size={13} className="text-gray-400" />
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={collCharge}
                      onChange={(e) => setCollCharge(e.target.value)}
                      className="flex-1 text-xs bg-transparent outline-none text-gray-800"
                    />
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Internal Remarks{" "}
                  <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  rows={2}
                  placeholder="Reason for edit, notes…"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-xs text-gray-800 placeholder-gray-400 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none transition-all"
                />
              </div>

              {/* Summary box */}
              <div className="rounded-xl bg-gray-50 border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 space-y-2">
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Subtotal ({items.length} tests)</span>
                    <span className="font-medium">{fmt(subtotal)}</span>
                  </div>

                  {discAmt > 0 && (
                    <div className="flex justify-between text-xs text-amber-600">
                      <span>Discount</span>
                      <span className="font-medium">− {fmt(discAmt)}</span>
                    </div>
                  )}

                  {collAmt > 0 && (
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>Collection Charge</span>
                      <span className="font-medium">+ {fmt(collAmt)}</span>
                    </div>
                  )}
                </div>

                <div className="px-4 py-3 bg-gray-900 flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-300">
                    Final Amount
                  </span>
                  <span className="text-lg font-bold text-white">
                    {fmt(finalTotal)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 space-y-3">
          {/* Regenerate invoice toggle — only shown when payment is already done */}
          {isPaid && (
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-blue-100 bg-blue-50">
              <input
                type="checkbox"
                checked={regenerateInvoice}
                onChange={(e) => setRegenerateInvoice(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-blue-600"
              />
              <div>
                <p className="text-xs font-semibold text-blue-800">Regenerate invoice &amp; send to patient</p>
                <p className="text-[11px] text-blue-600 mt-0.5">
                  A new invoice will be generated with the updated amounts and sent to the patient via WhatsApp.
                </p>
              </div>
            </label>
          )}

          <div className="flex items-center justify-between gap-3">
          <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-3 py-1 flex items-center gap-1.5">
            <Icon name="Info" size={10} />
            {isPaid ? "Editing a paid order — amounts will be updated" : "Only editable while payment is pending"}
          </p>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-xs font-medium border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              disabled={saving || loading || items.length === 0}
              className="flex items-center gap-2 px-5 py-2 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Spin size={13} cls="border-white" />
              ) : (
                <Icon name="Save" size={13} />
              )}
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}